/**
 * Audio-recording format helpers — the fix for « le micro sur iPhone ».
 *
 * iPhone Safari's MediaRecorder cannot produce WebM (it records AAC in an
 * MP4/M4A container). Hardcoding `audio/webm` meant the blob was either
 * empty or mislabeled, so Whisper could not read it and Médard never
 * received the question. We instead pick the FIRST format the current
 * browser actually supports, and name the upload with the matching
 * extension so Whisper detects the container.
 */

// Ordered by preference: Opus/WebM (Chrome/Firefox/Android) first, then the
// MP4/AAC family (iOS Safari), then plain formats as a last resort.
export const RECORDER_MIME_CANDIDATES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/aac',
    'audio/mpeg',
    'audio/ogg;codecs=opus',
];

/**
 * Return the first candidate MIME type the browser can record, or '' when
 * none is advertised (let the browser pick its own default).
 * `isSupported` is MediaRecorder.isTypeSupported (injected for testability).
 */
export function pickRecorderMimeType(isSupported) {
    if (typeof isSupported !== 'function') return '';
    for (const type of RECORDER_MIME_CANDIDATES) {
        try {
            if (isSupported(type)) return type;
        } catch {
            /* isTypeSupported can throw on some engines — treat as unsupported */
        }
    }
    return '';
}

/**
 * Map a recorded blob's MIME type to a filename extension Whisper accepts
 * (webm, m4a, mp3, wav, ogg). Defaults to 'm4a' — the safe iOS container —
 * rather than the old 'mp3' guess, which never matched an iPhone recording.
 */
export function extForMimeType(type = '') {
    const t = (type || '').toLowerCase();
    if (t.includes('webm')) return 'webm';
    if (t.includes('ogg')) return 'ogg';
    if (t.includes('wav')) return 'wav';
    if (t.includes('mpeg') || t.includes('mp3')) return 'mp3';
    if (t.includes('mp4') || t.includes('m4a') || t.includes('aac') || t.includes('mp4a')) return 'm4a';
    return 'm4a';
}
